import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, UserPlus, Upload, FileText, CheckCircle2, Image as ImageIcon, X, AlertCircle, ChevronDown } from 'lucide-react';
import logo from '../../assets/logo.png';
import BackButton from '../../components/BackButton';
import AgreementModal from '../../components/AgreementModal';

import { API_URL as API } from '../../config/api';

const GREEN   = '#084734';
const LIME    = '#CEF17B';
const LIME_BG = '#DCEFB8';

export default function PartnerRegister() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '', businessName: '', phone: '', email: '', password: '', confirmPassword: ''
  });

  // KYC details state
  const [kycForm, setKycForm] = useState({
    documentType: 'Aadhaar Card',
    documentNumber: '',
    documentImage: '',
    documentFileName: '',
  });
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docUploadError, setDocUploadError] = useState('');

  const [showAgreementModal, setShowAgreementModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [bankForm, setBankForm] = useState({ accountHolderName: '', accountNumber: '', ifscCode: '', bankName: '', accountType: 'savings', upiId: '' });
  const [bankError, setBankError] = useState('');
  const [step2Errors, setStep2Errors] = useState({});

  const fieldErrorStyle = {
    color: '#dc2626',
    fontSize: '0.78rem',
    fontWeight: '700',
    marginBottom: '4px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  };

  const handleStep1Submit = (e) => {
    e.preventDefault();
    setFieldErrors({});
    setError('');

    const { name, phone, password, confirmPassword, email } = formData;
    let hasError = false;
    const errors = {};

    if (!name.trim()) {
      errors.name = 'Full name is required';
      hasError = true;
    }

    if (!phone) {
      errors.phone = 'Mobile number is required';
      hasError = true;
    } else if (!/^[6789]\d{9}$/.test(phone)) {
      errors.phone = 'Mobile number must start with 6, 7, 8, or 9 and be 10 digits';
      hasError = true;
    }

    if (!email.trim()) {
      errors.email = 'Email address is required';
      hasError = true;
    } else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email.trim())) {
      errors.email = 'Please enter a valid email address';
      hasError = true;
    }

    if (!password) {
      errors.password = 'Password is required';
      hasError = true;
    } else {
      const missing = [];
      if (password.length < 8) missing.push('min 8 characters');
      if (!/[A-Z]/.test(password)) missing.push('1 uppercase letter (A-Z)');
      if (!/[a-z]/.test(password)) missing.push('1 lowercase letter (a-z)');
      if (!/\d/.test(password)) missing.push('1 numeric digit (0-9)');
      if (!/[!@#$%^&*()_+\-=\[\]{};\':"\\|,.<>\/?]/.test(password)) missing.push('1 special character (!@#$%...)');

      if (missing.length > 0) {
        errors.password = `Missing: ${missing.join(', ')}`;
        hasError = true;
      }
    }

    if (!confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
      hasError = true;
    } else if (confirmPassword !== password) {
      errors.confirmPassword = 'Passwords do not match';
      hasError = true;
    }

    if (hasError) {
      setFieldErrors(errors);
      return;
    }

    setBankForm(f => ({ ...f, accountHolderName: name.trim() }));
    setStep(2);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check size max 10MB
    if (file.size > 10 * 1024 * 1024) {
      setDocUploadError('File exceeds 10MB limit. Please choose a smaller image.');
      return;
    }

    setUploadingDoc(true);
    setDocUploadError('');
    setStep2Errors(errs => ({ ...errs, documentImage: '' }));
    setBankError('');

    // Instant local preview for immediate visual feedback
    const localPreview = URL.createObjectURL(file);
    setKycForm(prev => ({
      ...prev,
      documentImage: localPreview,
      documentFileName: file.name
    }));

    try {
      const formPayload = new FormData();
      formPayload.append('file', file);

      const res = await axios.post(`${API}/upload/document`, formPayload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const serverUrl = res.data.url || res.data.path;
      setKycForm(prev => ({
        ...prev,
        documentImage: serverUrl,
        documentFileName: file.name
      }));
    } catch (err) {
      console.error("Document upload error:", err);
      const errMsg = err.response?.data?.msg || err.response?.data?.detail || err.message || 'Failed to upload document photo.';
      setDocUploadError(`Upload error: ${errMsg}. Please try again.`);
      setKycForm(prev => ({ ...prev, documentImage: '', documentFileName: '' }));
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleStep2Submit = (e) => {
    if (e) e.preventDefault();
    setBankError('');
    setDocUploadError('');
    setStep2Errors({});

    if (uploadingDoc) {
      setDocUploadError('Document is still uploading. Please wait a moment...');
      return;
    }

    const errors = {};
    let hasError = false;

    // ── 1. Validate KYC Details (All required) ──
    if (!kycForm.documentType) {
      errors.documentType = 'This field is required. Please select a document type.';
      hasError = true;
    }

    if (!kycForm.documentNumber || !kycForm.documentNumber.trim()) {
      errors.documentNumber = `This field is required. Please enter your ${kycForm.documentType || 'document'} number.`;
      hasError = true;
    }

    if (!kycForm.documentImage || kycForm.documentImage.startsWith('blob:')) {
      errors.documentImage = 'This field is required. Please upload a photo/scan of your document.';
      hasError = true;
    }

    // ── 2. Validate Bank Details (All required except UPI ID) ──
    if (!bankForm.accountHolderName || !bankForm.accountHolderName.trim()) {
      errors.accountHolderName = 'This field is required. Please enter account holder name.';
      hasError = true;
    } else if (/\d/.test(bankForm.accountHolderName)) {
      errors.accountHolderName = 'Account holder name cannot contain numeric values.';
      hasError = true;
    }

    if (!bankForm.accountNumber || !bankForm.accountNumber.trim()) {
      errors.accountNumber = 'This field is required. Please enter bank account number.';
      hasError = true;
    } else if (!/^\d{9,18}$/.test(bankForm.accountNumber.trim())) {
      errors.accountNumber = 'Bank account number must be between 9 and 18 digits.';
      hasError = true;
    }

    if (!bankForm.ifscCode || !bankForm.ifscCode.trim()) {
      errors.ifscCode = 'This field is required. Please enter IFSC code.';
      hasError = true;
    } else if (!/^[A-Za-z0-9]{6,14}$/.test(bankForm.ifscCode.trim())) {
      errors.ifscCode = 'Please enter a valid IFSC code (e.g. SBIN0001234).';
      hasError = true;
    }

    if (!bankForm.bankName || !bankForm.bankName.trim()) {
      errors.bankName = 'This field is required. Please enter bank name.';
      hasError = true;
    }

    if (!bankForm.accountType) {
      errors.accountType = 'This field is required. Please select account type.';
      hasError = true;
    }

    // ── 3. Optional UPI ID validation ──
    if (bankForm.upiId && bankForm.upiId.trim()) {
      const upi = bankForm.upiId.trim();
      if (!/^[\w.\-_]{2,256}@[a-zA-Z]{2,64}$/.test(upi)) {
        errors.upiId = 'Please enter a valid UPI ID (e.g. username@okhdfcbank or 9876543210@upi).';
        hasError = true;
      }
    }

    if (hasError) {
      setStep2Errors(errors);
      setBankError('Please fill in all required fields before completing registration.');
      return;
    }

    // Open the Venue Partner Agreement Modal
    setShowAgreementModal(true);
  };

  const handleFinalPartnerRegistration = async () => {
    setLoading(true);
    setError('');
    const { name, phone, password, email } = formData;

    try {
      const res = await axios.post(`${API}/auth/register-password`, {
        name: name.trim(),
        phone: `+91${phone}`,
        email: email.trim(),
        password,
        role: 'owner'
      });

      const token = res.data.token;
      const user = res.data.user;

      // 1. If bank details were provided, save them (including optional upiId)
      if (bankForm.accountNumber.trim()) {
        try {
          await axios.post(
            `${API}/wallet/bank-details`,
            {
              accountHolderName: bankForm.accountHolderName.trim(),
              accountNumber: bankForm.accountNumber.trim(),
              ifscCode: bankForm.ifscCode.trim().toUpperCase(),
              bankName: bankForm.bankName.trim(),
              accountType: bankForm.accountType || 'savings',
              upiId: bankForm.upiId ? bankForm.upiId.trim() : null
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } catch (bErr) {
          console.warn("Bank details save warning", bErr);
        }
      }

      // 2. If KYC details or document image were provided, submit KYC
      if (kycForm.documentType || kycForm.documentNumber || kycForm.documentImage) {
        try {
          await axios.post(
            `${API}/kyc`,
            {
              document_type: kycForm.documentType,
              document_number: kycForm.documentNumber.trim(),
              document_image: kycForm.documentImage,
              business_name: name.trim(),
              account_holder_name: bankForm.accountHolderName,
              bank_account_number: bankForm.accountNumber,
              ifsc_code: bankForm.ifscCode.trim().toUpperCase(),
              bank_name: bankForm.bankName,
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } catch (kErr) {
          console.warn("KYC submission warning", kErr);
        }
      }

      login(user, token);
      setShowAgreementModal(false);
      navigate('/partner/dashboard');
    } catch (err) {
      setError(err.response?.data?.msg || 'Registration failed. Please try again.');
      setShowAgreementModal(false);
      setStep(1);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: isMobile ? 'column' : 'row', fontFamily: "'Inter', sans-serif", overflowX: 'hidden', maxWidth: '100vw' }}>

      {/* ── LEFT PANEL ── */}
      <div style={{
        width: isMobile ? '100%' : '560px', maxWidth: '100%', background: '#fff',
        display: 'flex', flexDirection: 'column',
        padding: isMobile ? '2rem 1.25rem' : '3rem 3.5rem', justifyContent: 'center',
        boxSizing: 'border-box',
      }}>
        {/* Logo */}
        <div style={{ marginBottom: '1.25rem' }}>
          <BackButton />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '2.5rem' }}>
          <Link to="/"><img src={logo} alt="TurfX" style={{ height: '36px' }} /></Link>
          <span style={{ background: LIME_BG, color: GREEN, fontWeight: '800', fontSize: '0.75rem', letterSpacing: '1.5px', padding: '4px 10px', borderRadius: '6px' }}>PARTNER</span>
        </div>

        {/* Step indicator */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{ width: '26px', height: '26px', borderRadius: '50%', background: step >= 1 ? (step > 1 ? LIME_BG : GREEN) : '#EEF2E6', color: step > 1 ? GREEN : step === 1 ? LIME : '#374151', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: '800' }}>
              {step > 1 ? '✓' : '1'}
            </div>
            <span style={{ fontSize: '0.78rem', fontWeight: step === 1 ? '800' : '600', color: step === 1 ? '#161616' : '#374151' }}>Account Info</span>
          </div>
          <div style={{ flex: 1, height: '2px', background: step > 1 ? GREEN : '#EEF2E6', borderRadius: '2px', transition: 'background 0.3s' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{ width: '26px', height: '26px', borderRadius: '50%', background: step === 2 ? GREEN : '#EEF2E6', color: step === 2 ? LIME : '#374151', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: '800' }}>2</div>
            <span style={{ fontSize: '0.78rem', fontWeight: step === 2 ? '800' : '600', color: step === 2 ? '#161616' : '#374151' }}>KYC & Bank Details</span>
          </div>
        </div>

        {/* ── STEP 1: Account Info ── */}
        {step === 1 && (
          <>
            <h2 style={{ fontSize: '2rem', fontWeight: '800', color: '#161616', marginBottom: '8px', lineHeight: 1.2 }}>Partner Registration</h2>
            <p style={{ color: '#374151', fontWeight: '500', marginBottom: '2rem', fontSize: '0.95rem' }}>Join our network of premium venue partners.</p>

            {error && (
              <div style={{ background: '#fff1f2', color: '#be123c', padding: '14px 16px', borderRadius: '12px', marginBottom: '1.5rem', fontSize: '0.88rem', fontWeight: '600', border: '1px solid #fecdd3' }}>{error}</div>
            )}

            <form onSubmit={handleStep1Submit} style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
              <div>
                <label style={labelStyle}>Full Name *</label>
                {fieldErrors.name && <div style={fieldErrorStyle}>⚠️ {fieldErrors.name}</div>}
                <input
                  type="text" placeholder="John Doe" value={formData.name}
                  onChange={e => {
                    setFormData({ ...formData, name: e.target.value.replace(/[^a-zA-Z\s]/g, '') });
                    setFieldErrors(prev => ({ ...prev, name: '' }));
                  }}
                  style={{
                    ...inputStyle,
                    borderRadius: '12px',
                    border: `1.5px solid ${fieldErrors.name ? '#ef4444' : '#E5E7EB'}`,
                    padding: '14px 16px'
                  }}
                />
              </div>
              <div>
                <label style={labelStyle}>Mobile Number *</label>
                {fieldErrors.phone && <div style={fieldErrorStyle}>⚠️ {fieldErrors.phone}</div>}
                <div style={{ display: 'flex', borderRadius: '12px', border: `1.5px solid ${fieldErrors.phone ? '#ef4444' : '#E5E7EB'}`, overflow: 'hidden', background: '#fff' }}>
                  <div style={{ padding: '14px 16px', background: '#F9FAFB', borderRight: '1.5px solid #E5E7EB', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.95rem', fontWeight: '600', color: '#374151', whiteSpace: 'nowrap' }}>🇮🇳 +91</div>
                  <input
                    type="tel" placeholder="Enter your phone number" value={formData.phone} maxLength={10}
                    onChange={e => {
                      setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '') });
                      setFieldErrors(prev => ({ ...prev, phone: '' }));
                    }}
                    style={{ flex: 1, padding: '14px 16px', border: 'none', outline: 'none', fontSize: '0.95rem', fontWeight: '500', color: '#111827', background: '#fff' }}
                  />
                </div>
              </div>
              <div>
                <label style={labelStyle}>Email Address *</label>
                {fieldErrors.email && <div style={fieldErrorStyle}>⚠️ {fieldErrors.email}</div>}
                <input
                  type="email" placeholder="john@gmail.com" value={formData.email}
                  onChange={e => {
                    setFormData({ ...formData, email: e.target.value });
                    setFieldErrors(prev => ({ ...prev, email: '' }));
                  }}
                  style={{
                    ...inputStyle,
                    borderRadius: '12px',
                    border: `1.5px solid ${fieldErrors.email ? '#ef4444' : '#E5E7EB'}`,
                    padding: '14px 16px'
                  }}
                />
              </div>
              <div>
                <label style={labelStyle}>Create Password *</label>
                {fieldErrors.password && <div style={fieldErrorStyle}>⚠️ {fieldErrors.password}</div>}
                <div style={{ position: 'relative', borderRadius: '12px', border: `1.5px solid ${fieldErrors.password ? '#ef4444' : '#E5E7EB'}`, overflow: 'hidden' }}>
                  <input
                    type={showPassword ? 'text' : 'password'} placeholder="Create password" value={formData.password}
                    onChange={e => {
                      setFormData({ ...formData, password: e.target.value });
                      setFieldErrors(prev => ({ ...prev, password: '' }));
                    }}
                    style={{ width: '100%', padding: '14px 48px 14px 16px', border: 'none', outline: 'none', fontSize: '0.95rem', fontWeight: '500', color: '#111827', background: '#fff', boxSizing: 'border-box' }}
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#374151', display: 'flex', alignItems: 'center', padding: 0 }}>
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <div>
                <label style={labelStyle}>Confirm Password *</label>
                {fieldErrors.confirmPassword && <div style={fieldErrorStyle}>⚠️ {fieldErrors.confirmPassword}</div>}
                <div style={{ position: 'relative', borderRadius: '12px', border: `1.5px solid ${fieldErrors.confirmPassword ? '#ef4444' : '#E5E7EB'}`, overflow: 'hidden' }}>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'} placeholder="Confirm password" value={formData.confirmPassword}
                    onChange={e => {
                      setFormData({ ...formData, confirmPassword: e.target.value });
                      setFieldErrors(prev => ({ ...prev, confirmPassword: '' }));
                    }}
                    style={{ width: '100%', padding: '14px 48px 14px 16px', border: 'none', outline: 'none', fontSize: '0.95rem', fontWeight: '500', color: '#111827', background: '#fff', boxSizing: 'border-box' }}
                  />
                  <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#374151', display: 'flex', alignItems: 'center', padding: 0 }}>
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <button type="submit" style={{ width: '100%', background: LIME, color: GREEN, border: 'none', padding: '16px', borderRadius: '12px', cursor: 'pointer', fontWeight: '800', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: 'background 0.2s', marginTop: '0.5rem' }}>
                <UserPlus size={18} />
                Continue to KYC & Bank Details →
              </button>
            </form>

            <div style={{ textAlign: 'center', marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid #F3F4F6' }}>
              <Link to="/partner/login" style={{ color: GREEN, fontWeight: '800', textDecoration: 'underline', fontSize: '0.9rem' }}>Already have an account? Sign in</Link>
            </div>
          </>
        )}

        {/* ── STEP 2: KYC & Bank Details ── */}
        {step === 2 && (
          <>
            <h2 style={{ fontSize: '1.8rem', fontWeight: '800', color: '#161616', marginBottom: '6px', lineHeight: 1.2 }}>Partner KYC & Bank Details</h2>
            <p style={{ color: '#374151', fontWeight: '500', marginBottom: '1.25rem', fontSize: '0.88rem' }}>
              Provide your government ID and payout account for verification & settlements.
            </p>

            {bankError && (
              <div style={{ background: '#fff1f2', color: '#be123c', padding: '12px 16px', borderRadius: '10px', marginBottom: '1rem', fontSize: '0.85rem', fontWeight: '600', border: '1px solid #fecdd3' }}>{bankError}</div>
            )}
            {docUploadError && (
              <div style={{ background: '#fff1f2', color: '#be123c', padding: '12px 16px', borderRadius: '10px', marginBottom: '1rem', fontSize: '0.85rem', fontWeight: '600', border: '1px solid #fecdd3' }}>{docUploadError}</div>
            )}

            <form onSubmit={handleStep2Submit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              
              {/* ── SECTION 1: KYC IDENTITY DOCUMENTS ── */}
              <div style={{ background: '#F8FAF7', border: '1.5px solid #EEF2E6', borderRadius: '16px', padding: '1.25rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '1rem' }}>
                  <FileText size={18} color="#084734" />
                  <span style={{ fontWeight: '800', color: '#084734', fontSize: '0.95rem' }}>1. Identity Document (KYC)</span>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {/* Document Type Dropdown */}
                  <div>
                    <label style={labelStyle}>Document Type *</label>
                    <div style={{ position: 'relative' }}>
                      <select
                        value={kycForm.documentType}
                        onChange={e => {
                          setKycForm(f => ({ ...f, documentType: e.target.value }));
                          setStep2Errors(errs => ({ ...errs, documentType: '' }));
                          setBankError('');
                        }}
                        style={{
                          ...inputStyle,
                          borderRadius: '10px',
                          border: `1.5px solid ${step2Errors.documentType ? '#ef4444' : '#E5E7EB'}`,
                          padding: '12px 38px 12px 14px',
                          cursor: 'pointer',
                          appearance: 'none',
                          WebkitAppearance: 'none',
                          MozAppearance: 'none',
                          background: '#fff'
                        }}
                      >
                        <option value="Aadhaar Card">Aadhaar Card</option>
                        <option value="Passport">Passport</option>
                        <option value="Driving License">Driving License</option>
                        <option value="Voter ID">Voter ID</option>
                      </select>
                      <ChevronDown
                        size={18}
                        color="#084734"
                        style={{
                          position: 'absolute',
                          right: '12px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          pointerEvents: 'none'
                        }}
                      />
                    </div>
                    {step2Errors.documentType && (
                      <div style={{ ...fieldErrorStyle, marginTop: '4px' }}><AlertCircle size={14} /> {step2Errors.documentType}</div>
                    )}
                  </div>

                  {/* Document Number */}
                  <div>
                    <label style={labelStyle}>{kycForm.documentType} Number *</label>
                    <input
                      type="text"
                      placeholder={`Enter ${kycForm.documentType} number`}
                      value={kycForm.documentNumber}
                      onChange={e => {
                        setKycForm(f => ({ ...f, documentNumber: e.target.value }));
                        setStep2Errors(errs => ({ ...errs, documentNumber: '' }));
                        setBankError('');
                      }}
                      style={{
                        ...inputStyle, borderRadius: '10px',
                        border: `1.5px solid ${step2Errors.documentNumber ? '#ef4444' : '#E5E7EB'}`,
                        padding: '12px 14px'
                      }}
                    />
                    {step2Errors.documentNumber && (
                      <div style={{ ...fieldErrorStyle, marginTop: '4px' }}><AlertCircle size={14} /> {step2Errors.documentNumber}</div>
                    )}
                  </div>

                  {/* Document Photo / Image Upload */}
                  <div>
                    <label style={labelStyle}>Upload Document Photo / Scan *</label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/jpg,application/pdf"
                      onChange={(e) => {
                        handleFileUpload(e);
                        setStep2Errors(errs => ({ ...errs, documentImage: '' }));
                        setBankError('');
                      }}
                      style={{ display: 'none' }}
                    />

                    {kycForm.documentImage ? (
                      <div style={{
                        border: '1.5px solid #DCEFB8', background: '#F4FBF1',
                        borderRadius: '12px', padding: '12px', display: 'flex',
                        alignItems: 'center', justifyContent: 'space-between', gap: '12px'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', overflow: 'hidden' }}>
                          {(kycForm.documentFileName?.toLowerCase().endsWith('.pdf') || kycForm.documentImage?.toLowerCase().endsWith('.pdf')) ? (
                            <div style={{ width: '48px', height: '48px', borderRadius: '8px', background: '#FEE2E2', color: '#DC2626', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              <FileText size={24} />
                            </div>
                          ) : (
                            <img
                              src={kycForm.documentImage}
                              alt="Uploaded KYC"
                              style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #DCEFB8', flexShrink: 0 }}
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          )}
                          <div>
                            <div style={{ fontSize: '0.85rem', fontWeight: '800', color: '#084734', display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <CheckCircle2 size={14} color="#084734" /> Document Photo Attached
                            </div>
                            <div style={{ fontSize: '0.75rem', color: '#374151', marginTop: '2px', wordBreak: 'break-all' }}>
                              {kycForm.documentFileName || 'document_uploaded.jpg'}
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setKycForm(f => ({ ...f, documentImage: '', documentFileName: '' }));
                            if (fileInputRef.current) fileInputRef.current.value = '';
                          }}
                          style={{
                            background: '#FEE2E2', border: 'none', color: '#DC2626',
                            borderRadius: '8px', padding: '6px 10px', cursor: 'pointer',
                            fontSize: '0.75rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '4px'
                          }}
                        >
                          <X size={14} /> Remove
                        </button>
                      </div>
                    ) : (
                      <div
                        onClick={() => fileInputRef.current?.click()}
                        style={{
                          border: `2px dashed ${step2Errors.documentImage ? '#ef4444' : '#CBD5E1'}`,
                          borderRadius: '12px',
                          padding: '1.25rem', textAlign: 'center', cursor: 'pointer',
                          background: step2Errors.documentImage ? '#fff5f5' : '#fff',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={e => e.currentTarget.style.borderColor = step2Errors.documentImage ? '#dc2626' : '#084734'}
                        onMouseLeave={e => e.currentTarget.style.borderColor = step2Errors.documentImage ? '#ef4444' : '#CBD5E1'}
                      >
                        <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#F8FAF7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px', color: '#084734' }}>
                          <Upload size={20} />
                        </div>
                        <div style={{ fontSize: '0.85rem', fontWeight: '800', color: step2Errors.documentImage ? '#dc2626' : '#084734' }}>
                          {uploadingDoc ? 'Uploading document photo...' : 'Click to upload document photo *'}
                        </div>
                        <div style={{ fontSize: '0.72rem', color: '#64748B', marginTop: '4px' }}>
                          PNG, JPG, WebP or PDF (Max 10MB) · Saved in backend documents
                        </div>
                      </div>
                    )}
                    {step2Errors.documentImage && (
                      <div style={{ ...fieldErrorStyle, marginTop: '6px' }}><AlertCircle size={14} /> {step2Errors.documentImage}</div>
                    )}
                    {docUploadError && (
                      <div style={{ background: '#FEE2E2', color: '#DC2626', padding: '8px 12px', borderRadius: '8px', marginTop: '6px', fontSize: '0.8rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <AlertCircle size={14} /> {docUploadError}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* ── SECTION 2: BANK DETAILS ── */}
              <div style={{ background: '#F8FAF7', border: '1.5px solid #EEF2E6', borderRadius: '16px', padding: '1.25rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '1rem' }}>
                  <span style={{ fontSize: '1.1rem' }}>🏦</span>
                  <span style={{ fontWeight: '800', color: '#084734', fontSize: '0.95rem' }}>2. Bank Details (Payouts & Settlements)</span>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div>
                    <label style={labelStyle}>Account Holder Name *</label>
                    <input
                      type="text"
                      placeholder="As per bank records"
                      value={bankForm.accountHolderName}
                      onChange={e => {
                        setBankForm(f => ({ ...f, accountHolderName: e.target.value }));
                        setStep2Errors(errs => ({ ...errs, accountHolderName: '' }));
                        setBankError('');
                      }}
                      style={{
                        ...inputStyle, borderRadius: '10px',
                        border: `1.5px solid ${step2Errors.accountHolderName ? '#ef4444' : '#E5E7EB'}`,
                        padding: '12px 14px'
                      }}
                    />
                    {step2Errors.accountHolderName && (
                      <div style={{ ...fieldErrorStyle, marginTop: '4px' }}><AlertCircle size={14} /> {step2Errors.accountHolderName}</div>
                    )}
                  </div>

                  <div>
                    <label style={labelStyle}>Account Number *</label>
                    <input
                      type="text"
                      placeholder="Enter bank account number (9-18 digits)"
                      value={bankForm.accountNumber}
                      onChange={e => {
                        setBankForm(f => ({ ...f, accountNumber: e.target.value.replace(/\D/g, '') }));
                        setStep2Errors(errs => ({ ...errs, accountNumber: '' }));
                        setBankError('');
                      }}
                      style={{
                        ...inputStyle, borderRadius: '10px',
                        border: `1.5px solid ${step2Errors.accountNumber ? '#ef4444' : '#E5E7EB'}`,
                        padding: '12px 14px'
                      }}
                      maxLength={18}
                    />
                    {step2Errors.accountNumber && (
                      <div style={{ ...fieldErrorStyle, marginTop: '4px' }}><AlertCircle size={14} /> {step2Errors.accountNumber}</div>
                    )}
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '10px' }}>
                    <div>
                      <label style={labelStyle}>IFSC Code *</label>
                      <input
                        type="text"
                        placeholder="e.g. SBIN0001234"
                        value={bankForm.ifscCode}
                        onChange={e => {
                          setBankForm(f => ({ ...f, ifscCode: e.target.value.toUpperCase() }));
                          setStep2Errors(errs => ({ ...errs, ifscCode: '' }));
                          setBankError('');
                        }}
                        style={{
                          ...inputStyle, borderRadius: '10px',
                          border: `1.5px solid ${step2Errors.ifscCode ? '#ef4444' : '#E5E7EB'}`,
                          padding: '12px 14px'
                        }}
                        maxLength={14}
                      />
                      {step2Errors.ifscCode && (
                        <div style={{ ...fieldErrorStyle, marginTop: '4px' }}><AlertCircle size={14} /> {step2Errors.ifscCode}</div>
                      )}
                    </div>

                    <div>
                      <label style={labelStyle}>Bank Name *</label>
                      <input
                        type="text"
                        placeholder="e.g. State Bank of India, HDFC"
                        value={bankForm.bankName}
                        onChange={e => {
                          setBankForm(f => ({ ...f, bankName: e.target.value }));
                          setStep2Errors(errs => ({ ...errs, bankName: '' }));
                          setBankError('');
                        }}
                        style={{
                          ...inputStyle, borderRadius: '10px',
                          border: `1.5px solid ${step2Errors.bankName ? '#ef4444' : '#E5E7EB'}`,
                          padding: '12px 14px'
                        }}
                      />
                      {step2Errors.bankName && (
                        <div style={{ ...fieldErrorStyle, marginTop: '4px' }}><AlertCircle size={14} /> {step2Errors.bankName}</div>
                      )}
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '10px' }}>
                    <div>
                      <label style={labelStyle}>Account Type *</label>
                      <div style={{ position: 'relative' }}>
                        <select
                          value={bankForm.accountType}
                          onChange={e => {
                            setBankForm(f => ({ ...f, accountType: e.target.value }));
                            setStep2Errors(errs => ({ ...errs, accountType: '' }));
                            setBankError('');
                          }}
                          style={{
                            ...inputStyle,
                            borderRadius: '10px',
                            border: `1.5px solid ${step2Errors.accountType ? '#ef4444' : '#E5E7EB'}`,
                            padding: '12px 38px 12px 14px',
                            cursor: 'pointer',
                            appearance: 'none',
                            WebkitAppearance: 'none',
                            MozAppearance: 'none',
                            background: '#fff'
                          }}
                        >
                          <option value="savings">Savings Account</option>
                          <option value="current">Current Account</option>
                        </select>
                        <ChevronDown
                          size={18}
                          color="#084734"
                          style={{
                            position: 'absolute',
                            right: '12px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            pointerEvents: 'none'
                          }}
                        />
                      </div>
                      {step2Errors.accountType && (
                        <div style={{ ...fieldErrorStyle, marginTop: '4px' }}><AlertCircle size={14} /> {step2Errors.accountType}</div>
                      )}
                    </div>

                    <div>
                      <label style={labelStyle}>
                        UPI ID <span style={{ color: '#64748B', fontWeight: '500', textTransform: 'none' }}>(Optional)</span>
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. username@okhdfcbank"
                        value={bankForm.upiId}
                        onChange={e => {
                          setBankForm(f => ({ ...f, upiId: e.target.value.trim() }));
                          setStep2Errors(errs => ({ ...errs, upiId: '' }));
                          setBankError('');
                        }}
                        style={{
                          ...inputStyle, borderRadius: '10px',
                          border: `1.5px solid ${step2Errors.upiId ? '#ef4444' : '#E5E7EB'}`,
                          padding: '12px 14px'
                        }}
                      />
                      {step2Errors.upiId && (
                        <div style={{ ...fieldErrorStyle, marginTop: '4px' }}><AlertCircle size={14} /> {step2Errors.upiId}</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || uploadingDoc}
                style={{
                  width: '100%', background: loading || uploadingDoc ? '#b5d96a' : LIME,
                  color: GREEN, border: 'none', padding: '16px', borderRadius: '12px',
                  cursor: loading || uploadingDoc ? 'not-allowed' : 'pointer',
                  fontWeight: '800', fontSize: '1rem', marginTop: '0.25rem'
                }}
              >
                {loading ? 'Opening Agreement...' : '✓ Review Agreement & Complete Registration'}
              </button>
            </form>
          </>
        )}

        <p style={{ color: '#374151', fontSize: '0.78rem', textAlign: 'center', marginTop: '2.5rem' }}>
          © {new Date().getFullYear()} MetaQode Technologies Pvt. Ltd.
        </p>
      </div>

      {/* ── RIGHT PANEL ── */}
      {!isMobile && (
        <div style={{
          flex: 1, background: GREEN,
          display: 'flex', flexDirection: 'column', justifyContent: 'center',
          padding: '5rem', position: 'relative', overflow: 'hidden',
        }}>
        {/* Grid line pattern */}
        <div style={{
          position: 'absolute', inset: 0,
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)
          `,
          backgroundSize: '80px 80px',
          pointerEvents: 'none',
        }} />

        <div style={{ position: 'relative', zIndex: 2 }}>
          <p style={{ color: LIME, fontWeight: '700', fontSize: '0.8rem', letterSpacing: '2px', marginBottom: '1.5rem' }}>
            — VENUE PARTNERS
          </p>
          <h1 style={{ fontSize: '3rem', fontWeight: '900', color: '#fff', lineHeight: 1.15, marginBottom: '1.5rem' }}>
            Transform Your<br />
            <span style={{ color: LIME }}>Venue Business.</span>
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: '1rem', lineHeight: 1.7, maxWidth: '480px', marginBottom: '3rem' }}>
            Join 500+ venue partners who have scaled their operations and maximized revenue with TurfX.
          </p>

          {/* Feature list */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', marginBottom: '4rem' }}>
            {[
              { n: '1', title: 'Rapid Onboarding', sub: 'Get your venue live and accepting bookings within 24 hours.' },
              { n: '2', title: 'Performance-Based Pricing', sub: 'Zero setup fees. Pay only when you receive confirmed bookings.' },
              { n: '3', title: 'Business Intelligence', sub: 'Access comprehensive analytics on revenue trends and customer behavior.' },
            ].map(f => (
              <div key={f.n} style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                <div style={{
                  width: '36px', height: '36px', borderRadius: '50%',
                  background: LIME, color: GREEN,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.85rem', fontWeight: '900', flexShrink: 0,
                }}>{f.n}</div>
                <div>
                  <div style={{ fontWeight: '700', color: '#fff', fontSize: '1rem' }}>{f.title}</div>
                  <div style={{ color: 'rgba(255,255,255,0.55)', marginTop: '4px', fontSize: '0.88rem', lineHeight: 1.5 }}>{f.sub}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Stats badges */}
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            {[
              { val: '500+', label: 'Partner venues' },
              { val: '10K+', label: 'Active players' },
              { val: '4.8★', label: 'Avg rating' },
            ].map(s => (
              <div key={s.val} style={{
                background: 'rgba(206,241,123,0.12)', border: '1px solid rgba(206,241,123,0.25)',
                borderRadius: '50px', padding: '10px 20px',
                display: 'flex', alignItems: 'center', gap: '8px',
              }}>
                <span style={{ color: LIME, fontWeight: '800', fontSize: '1rem' }}>{s.val}</span>
                <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>)}

      {/* ── Mandatory Venue Partner Agreement Modal ── */}
      <AgreementModal
        isOpen={showAgreementModal}
        onClose={() => setShowAgreementModal(false)}
        onAccept={handleFinalPartnerRegistration}
        agreementType="partner"
        mandatoryAccept={true}
        postReg={false}
        actionButtonText="Accept Agreement & Complete Registration →"
        loadingAction={loading}
      />
    </div>
  );
}

const labelStyle = {
  display: 'block', fontSize: '0.75rem', fontWeight: '700',
  color: '#374151', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.8px',
};

const inputStyle = {
  width: '100%', padding: '14px 16px',
  border: '1.5px solid #E5E7EB', borderRadius: '12px',
  fontSize: '0.95rem', fontWeight: '500', color: '#111827',
  outline: 'none', background: '#fff', boxSizing: 'border-box',
};
